import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';

const BRAND_TEXT = 'LEARN WITH TRUST';
const SESSION_KEY = 'lwt_brand_reveal_seen';

/**
 * Minimalist premium brand reveal overlay.
 * Sequence (≈1.4s total):
 *   0.0-0.5s  Deep-navy backdrop fades in
 *   0.3-0.9s  Logo icon scales + soft indigo glow expands
 *   0.6-1.1s  "LEARN WITH TRUST" letters cascade up (stagger 0.04s)
 *   1.2-1.4s  Whole overlay fades + slight scale-up exit
 *
 * Shows ONCE per browser session via sessionStorage, so navigation
 * within the app doesn't replay it.
 */
const BrandReveal = () => {
  // Decide visibility synchronously in render so React 18 StrictMode's
  // double-invocation of effects cannot leave the overlay stuck on.
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return !sessionStorage.getItem(SESSION_KEY); } catch { return false; }
  });

  useEffect(() => {
    if (!show) return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {}
    const t = setTimeout(() => setShow(false), 1400);
    return () => clearTimeout(t);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="brand-reveal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0F172A] pointer-events-none"
          style={{ willChange: 'opacity, transform' }}
          aria-hidden
          data-testid="brand-reveal-overlay"
        >
          {/* Subtle radial glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.5, scale: 1.4 }}
            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
            className="absolute w-[520px] h-[520px] rounded-full bg-indigo-500/30 blur-3xl pointer-events-none"
            aria-hidden
          />

          <div className="relative flex flex-col items-center gap-6">
            {/* Logo icon with glow halo (separate elements to avoid filter conflict) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6, filter: 'blur(8px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-24 h-24 flex items-center justify-center"
              data-testid="brand-reveal-logo"
            >
              <span
                className="absolute inset-0 rounded-3xl blur-2xl bg-indigo-400/40"
                aria-hidden
              />
              <img
                src="/lwt-icon-new.png"
                alt=""
                className="relative w-20 h-20"
                style={{ filter: 'invert(1) brightness(2)' }}
              />
            </motion.div>

            {/* Wordmark with stagger — forced LTR so Arabic UI doesn't reverse the brand letters */}
            <motion.div
              initial="hidden"
              animate="visible"
              transition={{ delayChildren: 0.55, staggerChildren: 0.035 }}
              className="brand-wordmark text-3xl sm:text-4xl text-white tracking-[0.18em]"
              style={{ direction: 'ltr', unicodeBidi: 'isolate' }}
              dir="ltr"
              data-testid="brand-reveal-wordmark"
            >
              {BRAND_TEXT.split('').map((ch, i) => (
                <motion.span
                  key={`${ch}-${i}`}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block"
                  style={{ whiteSpace: ch === ' ' ? 'pre' : 'normal' }}
                >
                  {ch}
                </motion.span>
              ))}
            </motion.div>

            {/* Tagline (very subtle) */}
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ duration: 0.5, delay: 0.95 }}
              className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-indigo-300 font-light"
              data-testid="brand-reveal-tagline"
            >
              Trusted Education Platform
            </motion.span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BrandReveal;
